import { debounce } from "lodash-es";
import type { PropsWithChildren, ReactNode } from "react";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from "react";
import { Link, type To } from "react-router";
import { twJoin } from "tailwind-merge";

import { ContextMenu, type ContextMenuItem } from "@/design-system/ContextMenu";
import { twResolve } from "@/helpers/tw-resolve";

export type Breadcrumb = {
  name: string;
} & (
  | {
      to: To;
      callback?: never;
    }
  | {
      callback: () => void;
      to?: never;
    }
  | {
      to?: never;
      callback?: never;
    }
);

export type BreadcrumbsProps = {
  pages: Breadcrumb[];
  overflowLabel: string;
};

type State = {
  hiddenItems: Breadcrumb[];
  visibleItems: Breadcrumb[];
};

type Action = {
  type: "remove" | "reset";
  payload?: Breadcrumb[];
};

export function Breadcrumbs({ pages, overflowLabel }: BreadcrumbsProps): ReactNode {
  const rootRef = useRef<HTMLElement>(null);
  const [state, dispatch] = useReducer(reducer, pages, (initialPages) => ({
    hiddenItems: [],
    visibleItems: initialPages,
  }));

  const transform = useCallback(() => {
    if (rootRef.current == null) {
      return;
    }

    const { clientWidth, scrollWidth } = rootRef.current;
    if (scrollWidth > clientWidth && state.visibleItems.length > 2) {
      dispatch({ type: "remove" });
    }
  }, [state]);

  const debouncedTransform = useMemo(
    () =>
      debounce(() => {
        dispatch({ type: "reset", payload: pages });
      }, 200),
    [pages],
  );

  useLayoutEffect(() => {
    transform();
  }, [transform]);

  useEffect(() => {
    dispatch({ type: "reset", payload: pages });
    // Pages can be recreated with the same values by translation and route consumers.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pages)]);

  useEffect(() => {
    window.addEventListener("resize", debouncedTransform);

    return () => window.removeEventListener("resize", debouncedTransform);
  }, [debouncedTransform]);

  const overflowItems = state.hiddenItems.map(toContextMenuItem);

  return (
    <nav className="flex w-full" ref={rootRef} aria-label="Breadcrumb" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center">
        {state.visibleItems.map((page, index, { length }) => {
          const isLastItem = length - 1 === index;

          return (
            <Fragment key={page.name}>
              {state.hiddenItems.length > 0 && index === 1 ? (
                <Item>
                  <Separator />
                  <ContextMenu
                    items={overflowItems}
                    triggerComponent={
                      <button aria-label={overflowLabel} className="breadcrumb-overflow-trigger" type="button">
                        <span className="text-caption text-text-brand">...</span>
                      </button>
                    }
                  />
                </Item>
              ) : null}
              <Item>
                {index !== 0 ? <Separator /> : null}
                <PageLink isLastItem={isLastItem} page={page} />
              </Item>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function Item({ children }: PropsWithChildren): ReactNode {
  return <li className="flex items-center">{children}</li>;
}

function Separator(): ReactNode {
  return (
    <span className="px-space-sm text-heading-4 text-text-primary" aria-hidden="true">
      /
    </span>
  );
}

function PageLink({ isLastItem, page }: { isLastItem: boolean; page: Breadcrumb }): ReactNode {
  const content = (
    <span
      className={twResolve("text-heading-4 whitespace-nowrap", isLastItem ? "text-text-primary" : "text-text-tertiary")}
    >
      {page.name}
    </span>
  );
  const actionClasses = twJoin("breadcrumb-action-focus", isLastItem ? "pointer-events-none" : "cursor-pointer");

  if ("to" in page && page.to !== undefined) {
    return (
      <Link
        className={twJoin(actionClasses, "hover:text-text-primary hover:underline")}
        data-testid="breadcrumbs-link"
        to={page.to}
        aria-current={isLastItem ? "page" : undefined}
      >
        {content}
      </Link>
    );
  }

  if ("callback" in page && page.callback !== undefined) {
    return (
      <button
        className={actionClasses}
        type="button"
        data-testid="breadcrumbs-link"
        aria-current={isLastItem ? "page" : undefined}
        onClick={page.callback}
      >
        {content}
      </button>
    );
  }

  return content;
}

function toContextMenuItem(breadcrumb: Breadcrumb): ContextMenuItem {
  const labels = { default: breadcrumb.name };

  if ("to" in breadcrumb && breadcrumb.to !== undefined) {
    return {
      labels,
      value: breadcrumb.name,
      type: "link",
      href: breadcrumb.to,
    };
  }

  if ("callback" in breadcrumb && breadcrumb.callback !== undefined) {
    return {
      labels,
      value: breadcrumb.name,
      type: "button",
      callbackFn: breadcrumb.callback,
    };
  }

  return {
    labels,
    value: breadcrumb.name,
    type: "button",
    isDisabled: true,
    callbackFn: () => {},
  };
}

function reducer(state: State, action: Action): State {
  if (action.type === "reset") {
    return {
      hiddenItems: [],
      visibleItems: [...(action.payload ?? [])],
    };
  }

  if (state.visibleItems.length === 2) {
    return state;
  }

  const visibleItems = [...state.visibleItems];
  const [hiddenItem] = visibleItems.splice(1, 1);

  return {
    hiddenItems: [...state.hiddenItems, hiddenItem],
    visibleItems,
  };
}
